import { useState, useEffect } from 'react';
import {
  IconCashBanknoteFilled,
  IconCreditCardFilled,
  IconHome2Filled,
  IconArchiveFilled,
  IconMotorbikeFilled,
  IconUser,
  IconUserPlus,
  IconX,
  IconAt,
  IconFileDownloadFilled,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import CloseOrden from './CloseOrden';
import { apiFetch } from '../api/client';
import { printTicket } from '../Utils/printer';

const SEARCH_DELAY_MS = 2000;
const FONT = '"Public Sans", sans-serif';
const PANEL = { backgroundColor: 'rgba(255, 255, 255, 0.04)' };
const LABEL = { fontSize: '12px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT };

function Panel({ children, className = '', style }) {
  return (
    <div className={`rounded-3xl p-4 ${className}`} style={{ ...PANEL, ...style }}>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, strong = false, accent = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        style={{
          fontSize: strong ? '14px' : '13px',
          fontWeight: strong ? 600 : 500,
          color: strong ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.45)',
          fontFamily: FONT,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: strong ? '17px' : '13px',
          fontWeight: strong ? 600 : 500,
          color: accent ? '#30d158' : strong ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
          fontFamily: FONT,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: strong ? '-0.01em' : 0,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ChoiceTile({ icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-col items-center justify-center gap-1.5 h-[78px] rounded-2xl border-none cursor-pointer transition-colors duration-200 ease-out active:scale-[0.97] ${
        active ? 'bg-white/[0.1]' : 'bg-white/[0.03] hover:bg-white/[0.06]'
      }`}
    >
      <Icon size={21} color={active ? '#0a84ff' : 'rgba(255, 255, 255, 0.4)'} />
      <span
        className="truncate max-w-full px-1"
        style={{
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: FONT,
          color: active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.5)',
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default function ModalCobrarPOS({
  isOpen,
  itemCount = 0,
  subtotal = 0,
  ivaIncluded = 0,
  total = 0,
  cartItems = [],
  facturacion = false,
  submitting = false,
  onClose,
  onConfirm,
}) {
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [orderType, setOrderType] = useState(null);
  const [query, setQuery] = useState('');
  const [customer, setCustomer] = useState(null);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTel, setNewTel] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [registering, setRegistering] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [promos, setPromos] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [discountPreview, setDiscountPreview] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (showSuccess) {
      printTicket([
        'NEBULA POS',
        'Pedido confirmado',
        `Total: $${Number(total).toFixed(2)}`,
        new Date().toLocaleString('es-MX'),
      ]).catch(() => {});
    }
  }, [showSuccess, total]);

  useEffect(() => {
    if (isOpen) {
      setShowSuccess(false);
      setStep(1);
      setPaymentMethod(null);
      setOrderType(null);
      setQuery('');
      setCustomer(null);
      setResults([]);
      setSearching(false);
      setSearched(false);
      setNewName('');
      setNewTel('');
      setNewEmail('');
      setRegistering(false);
      setCashReceived('');
      setPromos([]);
      setSelectedPromo(null);
      setDiscountPreview(null);
      setPromoError('');
    }
  }, [isOpen]);

  // Promociones disponibles (una sola por orden)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [cRes, iRes] = await Promise.all([
          apiFetch(`/category-discounts`),
          apiFetch(`/individual-discounts`),
        ]);
        const cats = cRes.ok ? await cRes.json() : [];
        const inds = iRes.ok ? await iRes.json() : [];
        if (!cancelled) {
          setPromos([
            ...cats.map((d) => ({ source: 'category', id: d.id, name: d.name })),
            ...inds.map((d) => ({ source: 'individual', id: d.id, name: d.name })),
          ]);
        }
      } catch {
        if (!cancelled) setPromos([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen]);

  const requestPreview = async (promo, currentOrderType) => {
    const res = await apiFetch(`/discounts/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: promo.source,
        discount_id: promo.id,
        order_type: currentOrderType,
        items: cartItems.map((it) => ({
          item_id: it.item_id,
          quantity: it.quantity || 1,
          unit_price: Number(it.basePrice) || 0,
          variants_cost: (Number(it.unitPrice) || 0) - (Number(it.basePrice) || 0),
        })),
      }),
    });
    if (!res.ok) {
      let msg = 'Esta promoción no aplica a la orden actual.';
      try {
        const err = await res.json();
        if (err.detail) msg = typeof err.detail === 'string' ? err.detail : msg;
      } catch {}
      throw new Error(msg);
    }
    const data = await res.json();
    if (!data.discount_amount || data.discount_amount <= 0) {
      throw new Error('Esta promoción no genera descuento en la orden actual.');
    }
    return data;
  };

  const handleSelectPromo = async (promo) => {
    const isSame = selectedPromo && selectedPromo.source === promo.source && selectedPromo.id === promo.id;
    if (isSame) {
      setSelectedPromo(null);
      setDiscountPreview(null);
      setPromoError('');
      return;
    }
    setSelectedPromo(promo);
    setDiscountPreview(null);
    setPromoError('');
    try {
      const data = await requestPreview(promo, orderType);
      setDiscountPreview(data);
    } catch (e) {
      setSelectedPromo(null);
      setPromoError(e.message || 'No se pudo aplicar la promoción.');
    }
  };

  // Si cambia el tipo de pedido, re-validar la promoción seleccionada.
  useEffect(() => {
    if (!isOpen || !selectedPromo || !orderType) return;
    let cancelled = false;
    requestPreview(selectedPromo, orderType)
      .then((data) => { if (!cancelled) setDiscountPreview(data); })
      .catch((e) => {
        if (cancelled) return;
        setSelectedPromo(null);
        setDiscountPreview(null);
        setPromoError(e.message || 'La promoción ya no aplica a este tipo de pedido.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderType]);

  useEffect(() => {
    if (!isOpen || customer) {
      setResults([]);
      setSearched(false);
      return;
    }
    const raw = query.trim().replace(/^@/, '');
    if (raw.length < 2) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/customers?q=${encodeURIComponent(raw)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResults(data);
        if (data.length === 1) {
          setCustomer(data[0]);
          setResults([]);
        }
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [isOpen, query, customer]);

  if (!isOpen) return null;

  const effectiveTotal = discountPreview
    ? Math.max(0, Number(total) - Number(discountPreview.discount_amount))
    : Number(total);

  const rawQuery = query.trim().replace(/^@/, '');
  const queryLength = rawQuery.length;
  const showRegister = !customer && searched && results.length === 0 && queryLength >= 2;

  const step1Valid = !!paymentMethod && !!orderType;
  const step2Valid = paymentMethod !== 'cash' || (cashReceived && Number(cashReceived) >= effectiveTotal);
  const canGoNext = step1Valid && step2Valid;
  const canConfirm = !!paymentMethod && !!orderType && !!customer && !submitting && step2Valid;

  const handleNext = () => {
    if (!canGoNext) return;
    setStep((s) => Math.min(3, s + 1));
  };

  const handleBack = () => {
    setStep((s) => Math.max(1, s - 1));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    const payload = {
      paymentMethod,
      orderType,
      customerId: customer?.id ?? null,
      cashReceived: paymentMethod === 'cash' ? Number(cashReceived) : null,
      cashChange: paymentMethod === 'cash' ? Math.max(0, Number(cashReceived) - effectiveTotal) : null,
      promo: selectedPromo,
    };
    setShowSuccess(true);
    onConfirm(payload);
  };

  const handleRegister = async () => {
    if (!newName.trim() || registering) return;
    setRegistering(true);
    try {
      const res = await apiFetch(`/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          username: rawQuery,
          tel: newTel.trim() || null,
          email: newEmail.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setCustomer(created);
      setNewName('');
      setNewTel('');
      setNewEmail('');
    } catch {
    } finally {
      setRegistering(false);
    }
  };

  const handleCashKeypad = (key) => {
    if (key === 'clear') {
      setCashReceived('');
      return;
    }
    if (key === 'back') {
      setCashReceived((prev) => prev.slice(0, -1));
      return;
    }
    setCashReceived((prev) => {
      let next = prev + key;
      if (key === '.') {
        if (next.split('.').length > 2) return prev;
        if (prev === '') next = '0.';
      }
      const cleaned = next.replace(/[^0-9.]/g, '');
      if (cleaned.split('.').length > 2) return prev;
      return cleaned;
    });
  };

  const methodLabel =
    paymentMethod === 'cash' ? 'Efectivo' : paymentMethod === 'debit' ? 'Débito' : paymentMethod === 'credit' ? 'Crédito' : '';

  const change = cashReceived ? Math.max(0, Number(cashReceived) - effectiveTotal) : 0;
  const hasEnoughCash = Boolean(cashReceived) && Number(cashReceived) >= effectiveTotal;

  const renderLeftStatic = () => (
    <div className="flex flex-col gap-4 w-full min-[900px]:w-[300px] shrink-0 min-[900px]:border-r min-[900px]:border-white/[0.16] min-[900px]:pr-6">
      <Panel>
        <h3
          className="font-semibold m-0"
          style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
        >
          Resumen
        </h3>
        <div className="flex flex-col gap-2 mt-3">
          <SummaryRow label="Fecha" value={new Date().toLocaleDateString('es-MX')} />
          <SummaryRow
            label="Hora"
            value={new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
          />
          <SummaryRow label="Artículos" value={itemCount} />
          <SummaryRow label="Subtotal" value={`$${Number(subtotal).toFixed(2)}`} />
          {facturacion && (
            <SummaryRow label="IVA (16%)" value={`$${Number(ivaIncluded).toFixed(2)}`} />
          )}
          {discountPreview && (
            <SummaryRow
              label={discountPreview.name}
              value={`-$${Number(discountPreview.discount_amount).toFixed(2)}`}
              accent
            />
          )}
          <div className="pt-1">
            <SummaryRow label="Total" value={`$${effectiveTotal.toFixed(2)}`} strong />
          </div>
        </div>
      </Panel>

      <Panel>
        <h3
          className="font-semibold m-0"
          style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
        >
          Método de pago
        </h3>
        <div className="grid grid-cols-3 gap-2 mt-2.5">
          <ChoiceTile
            icon={IconCashBanknoteFilled}
            active={paymentMethod === 'cash'}
            onClick={() => { setPaymentMethod('cash'); if (step === 1) setStep(2); }}
            label="Efectivo"
          />
          <ChoiceTile
            icon={IconCreditCardFilled}
            active={paymentMethod === 'debit'}
            onClick={() => { setPaymentMethod('debit'); if (step === 1) setStep(2); }}
            label="Débito"
          />
          <ChoiceTile
            icon={IconCreditCardFilled}
            active={paymentMethod === 'credit'}
            onClick={() => { setPaymentMethod('credit'); if (step === 1) setStep(2); }}
            label="Crédito"
          />
        </div>

        <h3
          className="font-semibold m-0 mt-5"
          style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
        >
          Tipo de pedido
        </h3>
        <div className="grid grid-cols-3 gap-2 mt-2.5">
          <ChoiceTile
            icon={IconHome2Filled}
            active={orderType === 'in_store'}
            onClick={() => setOrderType('in_store')}
            label="En tienda"
          />
          <ChoiceTile
            icon={IconArchiveFilled}
            active={orderType === 'pickup'}
            onClick={() => setOrderType('pickup')}
            label="Pickup"
          />
          <ChoiceTile
            icon={IconMotorbikeFilled}
            active={orderType === 'delivery'}
            onClick={() => setOrderType('delivery')}
            label="Domicilio"
          />
        </div>
      </Panel>
    </div>
  );

  const renderRightDynamic = () => (
    <div className="flex flex-col gap-4 flex-1 min-w-0">
      <Panel className="p-5">
        <span style={LABEL}>Total a cobrar</span>
        <div className="flex items-end justify-between gap-3 mt-1">
          <span
            style={{
              fontSize: '34px',
              lineHeight: '40px',
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ${effectiveTotal.toFixed(2)}
          </span>
          {methodLabel && (
            <span
              className="inline-flex items-center h-8 px-3 rounded-full shrink-0"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                fontSize: '12px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: FONT,
              }}
            >
              {methodLabel}
            </span>
          )}
        </div>
        {!paymentMethod && (
          <p
            className="m-0 mt-3"
            style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}
          >
            Selecciona un método de pago para continuar.
          </p>
        )}
      </Panel>

      {paymentMethod === 'cash' && (
        <Panel className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span style={LABEL}>Recibido</span>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span style={{ fontSize: '20px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.35)', fontFamily: FONT }}>
                  $
                </span>
                <span
                  className="truncate"
                  style={{
                    fontSize: '30px',
                    lineHeight: '36px',
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cashReceived || '0.00'}
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <span style={LABEL}>Cambio</span>
              <div
                style={{
                  fontSize: '22px',
                  lineHeight: '30px',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: hasEnoughCash ? '#30d158' : 'rgba(255, 255, 255, 0.35)',
                }}
              >
                ${change.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { key: '1', label: '1' },
              { key: '2', label: '2' },
              { key: '3', label: '3' },
              { key: '4', label: '4' },
              { key: '5', label: '5' },
              { key: '6', label: '6' },
              { key: '7', label: '7' },
              { key: '8', label: '8' },
              { key: '9', label: '9' },
              { key: '.', label: '.' },
              { key: '0', label: '0' },
              { key: 'back', label: '⌫' },
            ].map((btn) => (
              <button
                key={btn.key}
                type="button"
                onClick={() => handleCashKeypad(btn.key)}
                className="flex items-center justify-center rounded-2xl border-none cursor-pointer transition-colors duration-150 ease-out active:scale-[0.97] bg-white/[0.08] hover:bg-white/[0.12]"
                style={{
                  height: '52px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '19px',
                  fontWeight: 600,
                  fontFamily: FONT,
                }}
              >
                {btn.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleCashKeypad('clear')}
              className="col-span-3 flex items-center justify-center rounded-full border-none cursor-pointer transition-colors duration-150 bg-transparent hover:bg-white/[0.04]"
              style={{
                height: '44px',
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: FONT,
              }}
            >
              Borrar todo
            </button>
          </div>
        </Panel>
      )}

      {(paymentMethod === 'debit' || paymentMethod === 'credit') && (
        <Panel className="p-5">
          <div className="flex items-center gap-3">
            <span
              className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
            >
              <IconCreditCardFilled size={20} color="rgba(255, 255, 255, 0.6)" />
            </span>
            <div className="min-w-0">
              <p
                className="m-0"
                style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.92)', fontFamily: FONT }}
              >
                {paymentMethod === 'debit' ? 'Pago con tarjeta de débito' : 'Pago con tarjeta de crédito'}
              </p>
              <p
                className="m-0 mt-0.5"
                style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}
              >
                Confirma el pago para continuar.
              </p>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="flex flex-col gap-5 flex-1 min-w-0">
      <section>
        <h3
          className="font-semibold m-0"
          style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
        >
          Promociones
        </h3>
        {promos.length === 0 ? (
          <p
            className="m-0 mt-2.5"
            style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}
          >
            Sin promociones disponibles
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2.5">
            {promos.map((p) => {
              const selected = selectedPromo && selectedPromo.source === p.source && selectedPromo.id === p.id;
              return (
                <button
                  key={`${p.source}-${p.id}`}
                  type="button"
                  onClick={() => handleSelectPromo(p)}
                  aria-pressed={selected}
                  className={`h-10 px-4 rounded-full border-none cursor-pointer whitespace-nowrap transition-colors duration-200 ease-out active:scale-[0.98] ${
                    selected ? '' : 'bg-white/[0.05] hover:bg-white/[0.08]'
                  }`}
                  style={{
                    backgroundColor: selected ? '#0071e3' : undefined,
                    color: selected ? '#fff' : 'rgba(255, 255, 255, 0.65)',
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: FONT,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        )}
        {promoError && (
          <p
            className="m-0 mt-2"
            style={{ fontSize: '13px', fontWeight: 500, color: '#f87c2b', fontFamily: FONT }}
          >
            {promoError}
          </p>
        )}
      </section>

      <section>
        <h3
          className="font-semibold m-0"
          style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
        >
          Cliente
        </h3>

        <div
          className="flex items-center gap-2.5 h-12 rounded-full px-4 mt-2.5 transition-colors duration-200 focus-within:bg-white/[0.09]"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
        >
          <IconAt size={18} color="rgba(255, 255, 255, 0.45)" className="shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Usuario del cliente"
            autoComplete="off"
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: FONT,
              color: '#fff',
            }}
          />
          {searching && (
            <div
              className="shrink-0"
              style={{
                width: '18px',
                height: '18px',
                border: '2px solid rgba(255, 255, 255, 0.15)',
                borderTopColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '9999px',
                animation: 'spin 0.8s linear infinite',
              }}
            />
          )}
          {!searching && searched && results.length === 0 && queryLength >= 2 && !customer && (
            <IconCircleXFilled size={18} color="#f87c2b" className="shrink-0" />
          )}
          {!searching && customer && (
            <IconCircleCheckFilled size={18} color="#30d158" className="shrink-0" />
          )}
        </div>

        {customer ? (
          <div
            className="mt-3 rounded-2xl p-3.5"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
              >
                <IconUser size={18} color="rgba(255, 255, 255, 0.7)" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span
                  className="truncate"
                  style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', fontFamily: FONT }}
                >
                  {customer.username ? `@${customer.username}` : customer.name}
                </span>
                <span
                  className="truncate"
                  style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}
                >
                  {customer.username ? customer.name : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCustomer(null);
                  setQuery('');
                }}
                className="flex items-center justify-center shrink-0 w-9 h-9 rounded-full border-none cursor-pointer transition-colors duration-200 hover:bg-white/[0.12]"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.07)' }}
                aria-label="Quitar cliente"
              >
                <IconX size={15} color="rgba(255, 255, 255, 0.75)" />
              </button>
            </div>
          </div>
        ) : (
          <>
            {results.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-2">
                {results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setCustomer(c);
                      setResults([]);
                    }}
                    className="flex items-center gap-2.5 w-full h-12 px-4 rounded-2xl border-none cursor-pointer text-left transition-colors duration-200 bg-white/[0.04] hover:bg-white/[0.07]"
                  >
                    <IconUser size={15} color="rgba(255, 255, 255, 0.35)" className="shrink-0" />
                    <span
                      className="truncate"
                      style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
                    >
                      {c.username ? `@${c.username}` : c.name}
                    </span>
                    {c.username && (
                      <span
                        className="ml-auto shrink-0 truncate"
                        style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '13px', fontFamily: FONT }}
                      >
                        {c.name}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {showRegister && (
              <div
                className="mt-3 rounded-2xl p-4"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <IconUserPlus size={17} color="rgba(255, 255, 255, 0.35)" />
                  <span
                    style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.55)', fontFamily: FONT }}
                  >
                    Usuario nuevo — completa sus datos
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {[
                    { key: 'name', type: 'text', value: newName, onChange: (e) => setNewName(e.target.value), placeholder: 'Nombre del cliente' },
                    { key: 'tel', type: 'tel', value: newTel, onChange: (e) => setNewTel(e.target.value), placeholder: 'Teléfono (opcional)' },
                    { key: 'email', type: 'email', value: newEmail, onChange: (e) => setNewEmail(e.target.value), placeholder: 'Email (opcional)' },
                  ].map((field) => (
                    <input
                      key={field.key}
                      type={field.type}
                      value={field.value}
                      onChange={field.onChange}
                      onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                      placeholder={field.placeholder}
                      className="w-full h-11 rounded-full px-4 outline-none transition-colors duration-200 bg-white/[0.08] focus:bg-white/[0.12]"
                      style={{
                        border: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: FONT,
                        color: '#fff',
                        boxSizing: 'border-box',
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={handleRegister}
                    disabled={!newName.trim() || registering}
                    className="flex items-center justify-center h-11 px-5 rounded-full border-none font-semibold transition-all duration-200 ease-out active:scale-[0.98]"
                    style={{
                      backgroundColor: newName.trim() && !registering ? '#0071e3' : 'rgba(255, 255, 255, 0.08)',
                      color: newName.trim() && !registering ? '#fff' : 'rgba(255, 255, 255, 0.35)',
                      fontSize: '14px',
                      fontFamily: FONT,
                      cursor: newName.trim() && !registering ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {registering ? 'Registrando...' : 'Registrar'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <div
        className="inline-flex items-center gap-2 self-start h-9 px-3.5 rounded-full"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
      >
        <span
          className="flex items-center justify-center shrink-0 w-5 h-5 rounded-full"
          style={{ backgroundColor: facturacion ? '#0071e3' : 'rgba(255, 255, 255, 0.1)' }}
        >
          <IconFileDownloadFilled size={11} color={facturacion ? '#fff' : 'rgba(255, 255, 255, 0.5)'} />
        </span>
        <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.6)', fontFamily: FONT }}>
          {facturacion ? 'Con factura' : 'Sin factura'}
        </span>
      </div>
    </div>
  );

  const ghostStyle = {
    height: '48px',
    padding: '0 24px',
    borderRadius: '9999px',
    backgroundColor: '#fff',
    color: '#000',
    fontSize: '15px',
    fontWeight: 600,
    fontFamily: FONT,
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes successIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cierre de pedido"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '1080px',
          height: 'min(780px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
      >
        {showSuccess ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-10 px-8 py-10">
            <div
              className="flex flex-col items-center gap-4"
              style={{ animation: 'successIn 420ms ease-out' }}
            >
              <div
                className="flex items-center justify-center w-[88px] h-[88px] rounded-full"
                style={{ backgroundColor: 'rgba(48, 209, 88, 0.12)' }}
              >
                <IconCircleCheckFilled size={44} color="#30d158" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <span
                  style={{ fontSize: '26px', lineHeight: '32px', fontWeight: 600, letterSpacing: '-0.02em', color: 'rgba(255, 255, 255, 0.95)', fontFamily: FONT }}
                >
                  Pedido confirmado
                </span>
                <span
                  style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}
                >
                  Se levantó correctamente
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center rounded-full border-none cursor-pointer font-semibold transition-all duration-200 ease-out active:scale-[0.98] hover:brightness-110"
              style={{
                height: '48px',
                padding: '0 32px',
                backgroundColor: '#0071e3',
                color: '#fff',
                fontSize: '15px',
                letterSpacing: '-0.01em',
                fontFamily: FONT,
              }}
            >
              Continuar
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 shrink-0 px-5 pt-5 pb-4 sm:px-6 sm:pt-6 border-b border-white/[0.16]">
              <div className="min-w-0">
                <h2
                  className="font-semibold m-0 truncate"
                  style={{
                    fontSize: '24px',
                    lineHeight: '30px',
                    letterSpacing: '-0.02em',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontFamily: FONT,
                  }}
                >
                  Cierre de pedido
                </h2>
                <p
                  className="m-0 mt-1"
                  style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}
                >
                  {step === 3 ? 'Promociones y cliente' : 'Pago'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full border-none cursor-pointer transition-colors duration-200 hover:bg-white/[0.1]"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
              >
                <IconX size={18} color="rgba(255, 255, 255, 0.75)" stroke={2} />
              </button>
            </div>

            <div className="flex flex-col min-[900px]:flex-row gap-4 min-[900px]:gap-6 flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 pt-5 pb-5">
              {renderLeftStatic()}
              {step === 3 ? renderStep3() : renderRightDynamic()}
            </div>

            <div className="flex items-center justify-between gap-3 shrink-0 px-4 sm:px-6 py-4 sm:py-5 border-t border-white/[0.16]">
              <button
                type="button"
                onClick={() => {
                  if (step > 1) {
                    handleBack();
                    return;
                  }
                  onClose();
                }}
                className="flex items-center justify-center gap-1.5 transition-colors duration-200 active:scale-[0.98] hover:bg-white/[0.1]"
                style={ghostStyle}
              >
                <IconChevronLeft size={17} color="#000" />
                Volver
              </button>

              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className="flex items-center justify-center gap-1.5 rounded-full border-none font-semibold transition-all duration-200 ease-out active:scale-[0.98]"
                  style={{
                    height: '48px',
                    padding: '0 26px',
                    backgroundColor: canGoNext ? '#0071e3' : 'rgba(255, 255, 255, 0.08)',
                    color: canGoNext ? '#fff' : 'rgba(255, 255, 255, 0.35)',
                    fontSize: '15px',
                    fontFamily: FONT,
                    letterSpacing: '-0.01em',
                    cursor: canGoNext ? 'pointer' : 'not-allowed',
                  }}
                >
                  Siguiente
                  <IconChevronRight size={17} />
                </button>
              ) : (
                <CloseOrden onClick={handleConfirm} disabled={!canConfirm} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
